import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  ShoppingCart,
  DollarSign,
  Instagram,
  MessageCircle,
  AlertCircle,
  Info,
} from "lucide-react";
import { FIXED_PAGES_TOTAL, MAX_TOTAL_PAGES } from "@/data/agendaData";
import { SeccionAgenda } from "@/services/seccionesAgendaService";

// Lista de estampados (duplicada de PersonalizaAgenda - considerar mover a archivo compartido)
const ESTAMPADOS = [
  {
    id: "azul-hojas",
    name: "Azul Hojas",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/AZUL+HOJAS.jpg",
  },
  {
    id: "beige-floral",
    name: "Beige Floral",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/BEIGE FLORAL.jpg",
  },
  {
    id: "beige-flores-gris",
    name: "Beige Flores Gris Oscuro",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/BEIGE+FLORES+GRIS+OSCURO.jpg",
  },
  {
    id: "beige-hojas",
    name: "Beige Hojas",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/BEIGE+HOJAS.jpg",
  },
  {
    id: "camel-suave",
    name: "Camel Suave",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/CAMEL+SUAVE.jpg",
  },
  {
    id: "cerezas",
    name: "Cerezas",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/CEREZAS.jpg",
  },
  {
    id: "corazones-acanalados",
    name: "Corazones Acanalados",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/CORAZONES+ACANALADOS.jpg",
  },
  {
    id: "corazones-acuarela",
    name: "Corazones Acuarela",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/CORAZONES+ACUARELA.jpg",
  },
  {
    id: "florecitas-beige",
    name: "Florecitas Beige",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/FLORECITAS+BEIGS.jpg",
  },
  {
    id: "flores-negras",
    name: "Flores Negras",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/FLORES+NEGRAS.jpg",
  },
  {
    id: "mocca-floral",
    name: "Mocca Floral",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/MOCCA+FLORAL.jpg",
  },
  {
    id: "mocca-hojas",
    name: "Mocca Hojas",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/MOCCA+HOJAS.jpg",
  },
  {
    id: "negro-floral",
    name: "Negro Floral",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/NEGRO+FLORAL.jpg",
  },
  {
    id: "puntos-mocca",
    name: "Puntos Mocca",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/PUNTOS+MOCCA.jpg",
  },
  {
    id: "puntos-negros",
    name: "Puntos Negros",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/PUNTOS+NEGROS.jpg",
  },
  {
    id: "verde-floral",
    name: "Verde Floral",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/VERDE+FLORAL.jpg",
  },
  {
    id: "verde-suave",
    name: "Verde Suave",
    path: "https://viviconamoryarte-assets.s3.us-east-2.amazonaws.com/images/estampados-general/VERDE+SUAVE.jpg",
  },
];

interface PersonalizaAgendaResumenProps {
  selectedSections: SeccionAgenda[];
  variantesSeleccionadas: Record<string, string>;
  tableroBorrableType: string | null;
  qrInstagram: string | null;
  qrWhatsapp: string | null;
  personalizationType: "logo" | "estampado" | "estampado-personalizado" | null;
  logo: string | null;
  selectedColor: string | null;
  selectedEstampado: string | null;
  customEstampado?: string | null;
  totalPagesUsed: number;
  pagesRemaining: number;
  onAddToCart: () => void;
}

export const PersonalizaAgendaResumen = ({
  selectedSections,
  variantesSeleccionadas,
  tableroBorrableType,
  qrInstagram,
  qrWhatsapp,
  personalizationType,
  logo,
  selectedColor,
  selectedEstampado,
  customEstampado,
  totalPagesUsed,
  pagesRemaining,
  onAddToCart,
}: PersonalizaAgendaResumenProps) => {
  const PRECIO_FIJO = 35.0;

  // Calcular páginas personalizadas (excluyendo las fijas)
  const customPagesUsed = totalPagesUsed - FIXED_PAGES_TOTAL;

  // Validación: Debe tener tablero borrable obligatorio Y personalización visual
  const hasPersonalizacion =
    (personalizationType === "logo" &&
      logo !== null &&
      selectedColor !== null) ||
    (personalizationType === "estampado" && selectedEstampado !== null) ||
    (personalizationType === "estampado-personalizado" &&
      customEstampado !== null);

  const isValid = tableroBorrableType !== null && hasPersonalizacion;

  const getVarianteName = (section: SeccionAgenda): string | null => {
    const slug = variantesSeleccionadas[section.id];
    if (!slug) return null;
    return section.variantes.find((v) => v.slug === slug)?.nombre ?? slug;
  };

  return (
    <Card className="sticky top-4 shadow-lg">
      <CardHeader className="bg-gradient-to-br from-peach/20 to-primary/10">
        <CardTitle className="flex items-center gap-2 font-playfair text-brown-dark">
          📋 Resumen de tu Agenda
        </CardTitle>
        <CardDescription>Revisa tu configuración personalizada</CardDescription>
      </CardHeader>

      <CardContent className="p-6 space-y-4">
        {/* Contador de Páginas */}
        <div>
          <h4 className="font-semibold text-sm mb-3 text-brown-dark">
            📊 Páginas
          </h4>
          <div className="text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-brown-light/80">Fijas:</span>
              <span className="font-medium text-brown-dark">
                {FIXED_PAGES_TOTAL} páginas
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-brown-light/80">Personalizadas:</span>
              <span className="font-medium text-brown-dark">
                {customPagesUsed} páginas
              </span>
            </div>
            <Separator className="my-2" />
            <div className="flex justify-between font-semibold">
              <span className="text-brown-dark">Total:</span>
              <span className="text-brown-dark">
                {totalPagesUsed} / {MAX_TOTAL_PAGES}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-brown-light/80">Restantes:</span>
              <span
                className={`font-semibold ${
                  pagesRemaining < 50
                    ? "text-orange-600"
                    : pagesRemaining < 100
                    ? "text-yellow-600"
                    : "text-green-600"
                }`}
              >
                {pagesRemaining} páginas
              </span>
            </div>
          </div>

          {/* Barra de progreso mini */}
          <div className="mt-3">
            <div className="w-full bg-brown-light/20 rounded-full h-2 overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  pagesRemaining < 50
                    ? "bg-orange-500"
                    : pagesRemaining < 100
                    ? "bg-yellow-500"
                    : "bg-green-500"
                }`}
                style={{
                  width: `${Math.min(
                    (totalPagesUsed / MAX_TOTAL_PAGES) * 100,
                    100
                  )}%`,
                }}
              />
            </div>
          </div>

          {/* Callout informativo sobre páginas restantes */}
          {pagesRemaining > 0 && (
            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1.5">
                  <p className="font-semibold mb-1">
                    ¿Qué pasa con{" "}
                    {pagesRemaining === 1
                      ? "la página restante"
                      : `las ${pagesRemaining} páginas restantes`}
                    ?
                  </p>
                  <ul className="text-blue-600 dark:text-blue-400 leading-relaxed space-y-1 list-disc list-inside pl-1">
                    <li>
                      Las páginas que no selecciones se incluirán
                      automáticamente dentro de tu agenda.
                    </li>
                    <li>
                      Si eliges organización diaria, se sumarán como hojas de
                      organización diaria.
                    </li>
                    <li>
                      Si eliges organización semanal, se agregarán como hojas de
                      notas libres.
                    </li>
                    <li className="font-semibold">
                      Todas estas páginas ya están incluidas en el precio, sin
                      costo adicional.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        <Separator />

        {/* Tablero Borrable Obligatorio */}
        <div>
          <h4 className="font-semibold text-sm mb-3 text-brown-dark">
            🖊️ Tablero Borrable
          </h4>
          {tableroBorrableType ? (
            <div className="space-y-1">
              <Badge
                variant="secondary"
                className="bg-primary/20 text-brown-dark"
              >
                {tableroBorrableType === "semana"
                  ? "Semana"
                  : "Ideas / Bocetos"}
              </Badge>
              <p className="text-xs text-brown-light/70 mt-1">
                No suma páginas (portada interna)
              </p>
            </div>
          ) : (
            <Alert variant="destructive" className="py-2">
              <AlertCircle className="h-3 w-3" />
              <AlertDescription className="text-xs">
                Debes seleccionar un tipo de tablero borrable
              </AlertDescription>
            </Alert>
          )}
        </div>

        <Separator />

        {/* Secciones Seleccionadas */}
        <div>
          <h4 className="font-semibold text-sm mb-3 text-brown-dark flex items-center gap-2">
            📚 Secciones
            <Badge variant="outline" className="text-xs">
              {selectedSections.length}
            </Badge>
          </h4>

          {selectedSections.length > 0 ? (
            <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
              {selectedSections.map((section) => {
                const variantName = getVarianteName(section);

                return (
                  <div
                    key={section.id}
                    className="text-sm bg-brown-light/5 rounded-lg p-2 border border-brown-light/20"
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-base">{section.icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium leading-tight text-brown-dark truncate">
                          {section.nombre}
                        </p>

                        {/* Mostrar variante si aplica */}
                        {variantName && (
                          <Badge
                            variant="outline"
                            className="text-xs mt-1 bg-white"
                          >
                            {variantName}
                          </Badge>
                        )}

                        <p className="text-xs text-brown-light/70 mt-1">
                          {section.paginas} páginas
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-brown-light/60 italic text-center py-4 bg-brown-light/5 rounded-lg">
              No has seleccionado secciones adicionales
            </p>
          )}
        </div>

        <Separator />

        {/* Personalización Visual */}
        <div>
          <h4 className="font-semibold text-sm mb-3 text-brown-dark">
            🎨 Personalización Visual
          </h4>

          {personalizationType === "logo" && logo ? (
            <div className="space-y-2 bg-brown-light/5 rounded-lg p-3 border border-brown-light/20">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded border-2 border-brown-light/30 flex-shrink-0"
                  style={{ backgroundColor: selectedColor || "#ffffff" }}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-brown-dark">
                    Logo + Color
                  </p>
                  <p className="text-xs text-brown-light/70 font-mono truncate">
                    {selectedColor || "Sin color"}
                  </p>
                </div>
              </div>
            </div>
          ) : personalizationType === "estampado" && selectedEstampado ? (
            <div className="space-y-2 bg-brown-light/5 rounded-lg p-3 border border-brown-light/20">
              <p className="text-sm font-medium text-brown-dark">
                Estampado Seleccionado
              </p>
              <p className="text-xs text-brown-light/70">
                {ESTAMPADOS.find((e) => e.id === selectedEstampado)?.name}
              </p>
            </div>
          ) : personalizationType === "estampado-personalizado" &&
            customEstampado ? (
            <div className="space-y-2 bg-brown-light/5 rounded-lg p-3 border border-brown-light/20">
              <p className="text-sm font-medium text-brown-dark mb-2">
                Imagen Personalizada
              </p>
              <div className="relative w-full aspect-video rounded-lg overflow-hidden border-2 border-brown-light/30">
                <img
                  src={customEstampado}
                  alt="Imagen personalizada"
                  className="w-full h-full object-cover"
                />
              </div>
              <p className="text-xs text-brown-light/70 text-center">
                Tu imagen personalizada
              </p>
            </div>
          ) : (
            <p className="text-sm text-brown-light/60 italic text-center py-4 bg-brown-light/5 rounded-lg">
              Sin personalización visual
            </p>
          )}

          {/* QR Codes */}
          {(qrInstagram || qrWhatsapp) && (
            <div className="mt-3 space-y-2 bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-xs font-semibold text-brown-dark mb-2">
                Códigos QR incluidos:
              </p>
              {qrInstagram && (
                <div className="flex items-center gap-2 text-xs text-brown-dark">
                  <Instagram className="h-3 w-3 text-pink-600" />
                  <span>QR Instagram</span>
                </div>
              )}
              {qrWhatsapp && (
                <div className="flex items-center gap-2 text-xs text-brown-dark">
                  <MessageCircle className="h-3 w-3 text-green-600" />
                  <span>QR WhatsApp</span>
                </div>
              )}
            </div>
          )}
        </div>

        <Separator />

        {/* Precio Fijo */}
        <div className="bg-gradient-to-br from-primary/20 to-primary/10 p-4 rounded-lg border-2 border-primary/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-brown-dark" />
              <span className="font-bold text-lg text-brown-dark font-playfair">
                Precio Total:
              </span>
            </div>
            <span className="font-bold text-2xl text-brown-dark font-playfair">
              ${PRECIO_FIJO.toFixed(2)}
            </span>
          </div>
          <p className="text-xs text-brown-light/70 mt-2 text-center">
            Precio fijo incluye todas las personalizaciones
          </p>
        </div>

        {/* Validaciones */}
        {!isValid && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="text-xs space-y-1">
              {!tableroBorrableType && (
                <p>• Debes seleccionar un tipo de tablero borrable</p>
              )}
              {!hasPersonalizacion && (
                <p>
                  • Debes personalizar la portada (logo con color o estampado)
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Botón de Agregar al Carrito */}
        <Button
          className="w-full bg-primary hover:bg-primary-dark text-brown-dark font-semibold shadow-md transition-all duration-300 group relative overflow-hidden"
          size="lg"
          disabled={!isValid}
          onClick={onAddToCart}
        >
          <span className="relative z-10 flex items-center justify-center">
            <ShoppingCart className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
            Agregar al Carrito
          </span>
          {isValid && (
            <span className="absolute inset-0 bg-brown-dark/10 scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
          )}
        </Button>

        {isValid && (
          <p className="text-xs text-center text-brown-light/70 animate-pulse">
            ✨ Serás redirigido al carrito para completar tu compra
          </p>
        )}
      </CardContent>
    </Card>
  );
};
